package top.wcpe.allincore.ui.runtime.render.layer

import top.wcpe.allincore.ui.runtime.render.model.ImageStyle
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.ShadowParams
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import kotlin.jvm.JvmStatic

/**
 * 单条绘制记录：保存绘制命令的完整参数 + 绘制时刻的状态快照 + 全局序号。
 *
 * 由 [LayerBufferingBridge] 在每次 draw 调用时创建并缓存到对应层。使用 Builder 模式避免构造参数过多。
 *
 * 字段经 [Builder] 写入后只读，对外仅暴露方法式访问器（`x()` 等）；
 * 后缀 `Field` 的可变字段为 module 内部实现细节，外部一律经访问器读取。
 */
@Suppress("TooManyFunctions")
class DrawRecord private constructor() {
    // === 绘制命令 ===
    internal var typeField: RenderCommandType? = null
    internal var xField = 0f
    internal var yField = 0f
    internal var wField = 0f
    internal var hField = 0f
    internal var argbField = 0
    internal var textField: String? = null
    internal var scaleField = 0f
    internal var textureField: String? = null
    internal var fontNameField: String? = null
    internal var shadowField = false
    internal var boldField = false
    internal var italicField = false
    internal var lineThicknessField = 0f

    // 样式
    internal var styledFillField: StyledFill? = null
    internal var shadowParamsField: ShadowParams? = null
    internal var imageStyleField: ImageStyle? = null

    // 物品
    internal var itemIdField: String? = null

    // 物品 NBT（平台无关 SNBT 字符串）；null/空表示无 NBT
    internal var itemNbtField: String? = null
    internal var itemCountField = 0
    internal var enchantedField = false
    internal var showCountField = false

    // 数量徽标锚点与偏移（默认 "bottom-right" + 0/0 复刻 vanilla 右下角）
    internal var countAnchorField = DEFAULT_COUNT_ANCHOR
    internal var countOffsetXField = 0f
    internal var countOffsetYField = 0f

    // 物品背后纯背景色（ARGB）：随元素 opacity 变暗时用作半透明遮罩色（issue #68）。
    // alpha 为 0 表示「背后非纯色 / 透明槽 / 未知」，平台层回退旧 setShaderColor 淡化、不画遮罩。
    internal var backdropColorField = 0

    // 稳定调用方身份：仅进程内层缓冲回放使用，不序列化。
    internal var itemRenderIdentityField: Any? = null

    // 实体
    internal var entityRefField: String? = null
    internal var entityTypeField: String? = null

    // 实体显示名（写为预览虚拟实体 customName，供实体档案按名称匹配）；null/空表示不命名
    internal var entityNameField: String? = null
    internal var mouseXField = 0f
    internal var mouseYField = 0f
    internal var entityScaleField = 0f
    internal var rotationXField = 0f
    internal var rotationYField = 0f
    internal var followMouseField = false

    // 独立模型预览
    internal var modelSourceField: String? = null
    internal var modelBaseField: String? = null
    internal var modelTextureField: String? = null
    internal var modelInstanceKeyField: String? = null
    internal var modelScaleField = DEFAULT_MODEL_SCALE
    internal var modelRotationXField = 0f
    internal var modelRotationYField = 0f
    internal var modelInteractiveField = false
    internal var modelBackgroundColorField = 0

    // 特效
    internal var effectKindField: String? = null
    internal var effectColorAField = 0
    internal var effectColorBField = 0
    internal var effectIntensityField = 0f
    internal var effectFrequencyField = 0f
    internal var effectSpeedField = 0f
    internal var effectProgressField = 0f
    internal var effectCornerRadiiField: FloatArray? = null
    internal var effectCenterXField = 0f
    internal var effectCenterYField = 0f
    internal var effectRadiusField = 0f
    internal var effectModeField = 0
    internal var effectDensityField = 0f
    internal var effectSizeField = 0f
    internal var effectAnimatedField = false

    // UV 切片
    internal var uvXField = 0f
    internal var uvYField = 0f
    internal var uvWField = 0f
    internal var uvHField = 0f

    // MATERIAL_LAYER（程序化材质层）：kind 与 IR modifier 字段快照。
    internal var materialKindField: String? = null
    internal var materialFieldsField: Map<String, *>? = null

    // PARTICLE（FR-UI-031 粒子层）：元素跨帧稳定 id + 层序号 + 粒子参数快照（含鼠标坐标 / 交互态标志）。仅 PARTICLE 类型有意义。
    internal var particleElementIdField: String = ""
    internal var particleOrdinalField: Int = 0
    internal var particleSpecField: Map<String, *>? = null

    // DRAW_RICH_TEXT（内联富文本）：换行/裁剪可用宽度、是否多行软换行、多行行间 y 步进（像素）。
    // 文本/颜色/缩放/字体/阴影/粗斜复用 text/argb/scale/fontName/shadow/bold/italic 字段（与 DRAW_TEXT 一致，
    // 单条 DrawRecord 只承载一种命令类型，字段复用无歧义）；坐标复用 x/y。仅 DRAW_RICH_TEXT 类型有意义。
    internal var richTextMaxWidthField = 0f
    internal var richTextMultilineField = false
    internal var richTextLineStepField = 0f

    // FILTER_GROUP（CSS filter: blur）：高斯模糊半径（像素）。仅 FILTER_GROUP 类型有意义。
    internal var blurRadiusField = 0f

    // FILTER_GROUP（CSS filter: blur）：begin..end 之间捕获的子命令列表（含各自 StateSnapshot）。
    // 与 submitBatch 的 batchCommands 区分：blur 的 filterGroupCommands 是「整段子树录制」，
    // 回放时先 beginFilterGroup 把绘制重定向到离屏，再逐条回放，最后 endFilterGroup。
    internal var filterGroupCommandsField: List<DrawRecord>? = null

    // BACKDROP_FILTER（#8 backdrop-filter 背景模糊）：背景模糊半径（像素）。仅 BACKDROP_FILTER 类型有意义。
    internal var backdropBlurRadiusField = 0f

    // BLEND_GROUP（#8 mix-blend-mode）：混合模式序数（GuiBlendMode.ordinal()）。仅 BLEND_GROUP 类型有意义。
    internal var blendModeField = 0

    // BLEND_GROUP（#8 mix-blend-mode）：push..pop 之间捕获的子命令列表（含各自 StateSnapshot）。
    // 与 filterGroupCommands 同构：回放时先 pushBlendMode 切 GL 混合函数，再逐条回放，最后 popBlendMode。
    internal var blendGroupCommandsField: List<DrawRecord>? = null

    // HISTORY_GROUP（FR-UI-023 historyLayer）：元素跨帧稳定 id（环形缓冲键之一）。仅 HISTORY_GROUP 类型有意义。
    internal var historyElementIdField: String = ""

    // HISTORY_GROUP（FR-UI-023 historyLayer）：历史层参数快照（source/frames/downsample/decay/... ）。仅 HISTORY_GROUP 类型有意义。
    internal var historySpecField: Map<String, *>? = null

    // HISTORY_GROUP（FR-UI-023 historyLayer）：begin..end 之间捕获的子命令列表（含各自 StateSnapshot）。
    // 与 filterGroupCommands 同构：回放时先 beginHistoryCapture 重定向到离屏隔离靶，再逐条回放，最后 endHistoryCapture。
    internal var historyGroupCommandsField: List<DrawRecord>? = null

    // submitBatch 子命令
    internal var batchCommandsField: List<DrawRecord>? = null

    // styled 批命令原件：ROUNDED_RECT / GRADIENT / SHADOW 等需保留完整 RenderCommand，
    // 回放时重建单命令 RenderBatch 走 delegate.submitBatch 以保留圆角/边框/渐变/阴影/遮罩，
    // 而非降级成纯色 fillRect。
    internal var commandField: RenderCommand? = null

    // === 排序与状态 ===
    internal var sequenceField = 0
    internal var stateField: StateSnapshot? = null
    internal var nodePathField: String? = null

    /**
     * 屏障记录：本条不绘制任何东西，回放时只对 delegate 调一次 flush，
     * 把它之前录入的延迟顶点先提交上屏，与其后的绘制拉开批次；
     * 并按 [barrierPushZField] / [barrierPopZField] 顺带抬高 / 还原 z。
     *
     * 用途见 [LayerBufferingBridge.recordOverlayLift]。
     */
    internal var flushBarrierField = false

    /** 屏障回放时抬高的 z 增量；0 表示不抬。 */
    internal var barrierPushZField = 0f

    /** 屏障回放时是否还原一层 z（与 [barrierPushZField] 成对）。 */
    internal var barrierPopZField = false

    internal fun reset() {
        resetGeometryAndText()
        resetItemAndEntity()
        resetEffects()
        resetGroupsAndState()
    }

    private fun resetGeometryAndText() {
        typeField = null
        xField = 0f
        yField = 0f
        wField = 0f
        hField = 0f
        argbField = 0
        textField = null
        scaleField = 0f
        textureField = null
        fontNameField = null
        shadowField = false
        boldField = false
        italicField = false
        lineThicknessField = 0f
        styledFillField = null
        shadowParamsField = null
        imageStyleField = null
    }

    private fun resetItemAndEntity() {
        itemIdField = null
        itemNbtField = null
        itemCountField = 0
        enchantedField = false
        showCountField = false
        countAnchorField = DEFAULT_COUNT_ANCHOR
        countOffsetXField = 0f
        countOffsetYField = 0f
        backdropColorField = 0
        itemRenderIdentityField = null
        entityRefField = null
        entityTypeField = null
        entityNameField = null
        mouseXField = 0f
        mouseYField = 0f
        entityScaleField = 0f
        rotationXField = 0f
        rotationYField = 0f
        followMouseField = false
    }

    private fun resetEffects() {
        effectKindField = null
        effectColorAField = 0
        effectColorBField = 0
        effectIntensityField = 0f
        effectFrequencyField = 0f
        effectSpeedField = 0f
        effectProgressField = 0f
        effectCornerRadiiField = null
        effectCenterXField = 0f
        effectCenterYField = 0f
        effectRadiusField = 0f
        effectModeField = 0
        effectDensityField = 0f
        effectSizeField = 0f
        effectAnimatedField = false
        uvXField = 0f
        uvYField = 0f
        uvWField = 0f
        uvHField = 0f
    }

    private fun resetGroupsAndState() {
        materialKindField = null
        materialFieldsField = null
        particleElementIdField = ""
        particleOrdinalField = 0
        particleSpecField = null
        richTextMaxWidthField = 0f
        richTextMultilineField = false
        richTextLineStepField = 0f
        blurRadiusField = 0f
        filterGroupCommandsField = null
        backdropBlurRadiusField = 0f
        blendModeField = 0
        blendGroupCommandsField = null
        historyElementIdField = ""
        historySpecField = null
        historyGroupCommandsField = null
        batchCommandsField = null
        commandField = null
        sequenceField = 0
        stateField = null
        flushBarrierField = false
        barrierPushZField = 0f
        barrierPopZField = false
    }

    // 访问器
    fun type(): RenderCommandType? = typeField
    fun x(): Float = xField
    fun y(): Float = yField
    fun w(): Float = wField
    fun h(): Float = hField
    fun argb(): Int = argbField
    fun text(): String? = textField
    fun scale(): Float = scaleField
    fun texture(): String? = textureField
    fun fontName(): String? = fontNameField
    fun shadow(): Boolean = shadowField
    fun bold(): Boolean = boldField
    fun italic(): Boolean = italicField
    fun lineThickness(): Float = lineThicknessField
    fun styledFill(): StyledFill? = styledFillField
    fun shadowParams(): ShadowParams? = shadowParamsField
    fun imageStyle(): ImageStyle? = imageStyleField
    fun itemId(): String? = itemIdField
    fun itemNbt(): String? = itemNbtField
    fun itemCount(): Int = itemCountField
    fun enchanted(): Boolean = enchantedField
    fun showCount(): Boolean = showCountField
    fun countAnchor(): String = countAnchorField
    fun countOffsetX(): Float = countOffsetXField
    fun countOffsetY(): Float = countOffsetYField
    fun backdropColor(): Int = backdropColorField
    fun itemRenderIdentity(): Any? = itemRenderIdentityField
    fun entityRef(): String? = entityRefField
    fun entityType(): String? = entityTypeField
    fun entityName(): String? = entityNameField
    fun mouseX(): Float = mouseXField
    fun mouseY(): Float = mouseYField
    fun entityScale(): Float = entityScaleField
    fun rotationX(): Float = rotationXField
    fun rotationY(): Float = rotationYField
    fun followMouse(): Boolean = followMouseField
    fun modelSource(): String? = modelSourceField
    fun modelBase(): String? = modelBaseField
    fun modelTexture(): String? = modelTextureField
    fun modelInstanceKey(): String? = modelInstanceKeyField
    fun modelScale(): Float = modelScaleField
    fun modelRotationX(): Float = modelRotationXField
    fun modelRotationY(): Float = modelRotationYField
    fun modelInteractive(): Boolean = modelInteractiveField
    fun modelBackgroundColor(): Int = modelBackgroundColorField
    fun effectKind(): String? = effectKindField
    fun effectColorA(): Int = effectColorAField
    fun effectColorB(): Int = effectColorBField
    fun effectIntensity(): Float = effectIntensityField
    fun effectFrequency(): Float = effectFrequencyField
    fun effectSpeed(): Float = effectSpeedField
    fun effectProgress(): Float = effectProgressField
    fun effectCornerRadii(): FloatArray? = effectCornerRadiiField
    fun effectCenterX(): Float = effectCenterXField
    fun effectCenterY(): Float = effectCenterYField
    fun effectRadius(): Float = effectRadiusField
    fun effectMode(): Int = effectModeField
    fun effectDensity(): Float = effectDensityField
    fun effectSize(): Float = effectSizeField
    fun effectAnimated(): Boolean = effectAnimatedField
    fun uvX(): Float = uvXField
    fun uvY(): Float = uvYField
    fun uvW(): Float = uvWField
    fun uvH(): Float = uvHField
    fun materialKind(): String? = materialKindField
    fun materialFields(): Map<String, *>? = materialFieldsField
    fun particleElementId(): String = particleElementIdField
    fun particleOrdinal(): Int = particleOrdinalField
    fun particleSpec(): Map<String, *>? = particleSpecField
    fun richTextMaxWidth(): Float = richTextMaxWidthField
    fun richTextMultiline(): Boolean = richTextMultilineField
    fun richTextLineStep(): Float = richTextLineStepField
    fun blurRadius(): Float = blurRadiusField
    fun filterGroupCommands(): List<DrawRecord>? = filterGroupCommandsField
    fun backdropBlurRadius(): Float = backdropBlurRadiusField
    fun blendMode(): Int = blendModeField
    fun blendGroupCommands(): List<DrawRecord>? = blendGroupCommandsField
    fun historyElementId(): String = historyElementIdField
    fun historySpec(): Map<String, *>? = historySpecField
    fun historyGroupCommands(): List<DrawRecord>? = historyGroupCommandsField
    fun batchCommands(): List<DrawRecord>? = batchCommandsField
    fun command(): RenderCommand? = commandField
    fun sequence(): Int = sequenceField
    fun state(): StateSnapshot? = stateField
    fun nodePath(): String? = nodePathField

    /** 为当前记录及尚未带路径的嵌套子记录补充采集路径。 */
    internal fun assignNodePathIfAbsent(nodePath: String) {
        require(nodePath.isNotBlank()) { "运行时节点路径不能为空" }
        if (nodePathField == null) nodePathField = nodePath
        filterGroupCommandsField?.forEach { it.assignNodePathIfAbsent(nodePath) }
        blendGroupCommandsField?.forEach { it.assignNodePathIfAbsent(nodePath) }
        batchCommandsField?.forEach { it.assignNodePathIfAbsent(nodePath) }
    }

    /** 是否为屏障记录（不绘制，只在回放时提交先前批次并按需抬高 / 还原 z）。 */
    fun flushBarrier(): Boolean = flushBarrierField

    /** 屏障回放时抬高的 z 增量；0 表示不抬。 */
    fun barrierPushZ(): Float = barrierPushZField

    /** 屏障回放时是否还原一层 z。 */
    fun barrierPopZ(): Boolean = barrierPopZField

    /** Builder：逐字段累积后 [build] 产出不可再变的记录。 */
    @Suppress("TooManyFunctions")
    class Builder internal constructor(private val record: DrawRecord) {
        constructor() : this(DrawRecord())

        fun type(v: RenderCommandType?) = apply { record.typeField = v }
        fun command(v: RenderCommand?) = apply { record.commandField = v }
        fun pos(x: Float, y: Float, w: Float, h: Float) = apply {
            record.xField = x
            record.yField = y
            record.wField = w
            record.hField = h
        }

        fun argb(v: Int) = apply { record.argbField = v }
        fun text(v: String?) = apply { record.textField = v }
        fun scale(v: Float) = apply { record.scaleField = v }
        fun texture(v: String?) = apply { record.textureField = v }
        fun fontName(v: String?) = apply { record.fontNameField = v }
        fun shadow(v: Boolean) = apply { record.shadowField = v }
        fun bold(v: Boolean) = apply { record.boldField = v }
        fun italic(v: Boolean) = apply { record.italicField = v }
        fun lineThickness(v: Float) = apply { record.lineThicknessField = v }
        fun styledFill(v: StyledFill?) = apply { record.styledFillField = v }
        fun shadowParams(v: ShadowParams?) = apply { record.shadowParamsField = v }
        fun imageStyle(v: ImageStyle?) = apply { record.imageStyleField = v }
        fun itemId(v: String?) = apply { record.itemIdField = v }
        fun itemNbt(v: String?) = apply { record.itemNbtField = v }
        fun itemCount(v: Int) = apply { record.itemCountField = v }
        fun enchanted(v: Boolean) = apply { record.enchantedField = v }
        fun showCount(v: Boolean) = apply { record.showCountField = v }
        fun countAnchor(v: String?) = apply { record.countAnchorField = v ?: DEFAULT_COUNT_ANCHOR }
        fun countOffsetX(v: Float) = apply { record.countOffsetXField = v }
        fun countOffsetY(v: Float) = apply { record.countOffsetYField = v }
        fun backdropColor(v: Int) = apply { record.backdropColorField = v }
        fun itemRenderIdentity(v: Any?) = apply { record.itemRenderIdentityField = v }
        fun entityRef(v: String?) = apply { record.entityRefField = v }
        fun entityType(v: String?) = apply { record.entityTypeField = v }
        fun entityName(v: String?) = apply { record.entityNameField = v }
        fun mouseX(v: Float) = apply { record.mouseXField = v }
        fun mouseY(v: Float) = apply { record.mouseYField = v }
        fun entityScale(v: Float) = apply { record.entityScaleField = v }
        fun rotationX(v: Float) = apply { record.rotationXField = v }
        fun rotationY(v: Float) = apply { record.rotationYField = v }
        fun followMouse(v: Boolean) = apply { record.followMouseField = v }
        fun modelSource(v: String?) = apply { record.modelSourceField = v }
        fun modelBase(v: String?) = apply { record.modelBaseField = v }
        fun modelTexture(v: String?) = apply { record.modelTextureField = v }
        fun modelInstanceKey(v: String?) = apply { record.modelInstanceKeyField = v }
        fun modelScale(v: Float) = apply {
            record.modelScaleField = if (v.isFinite() && v > 0f) v else DEFAULT_MODEL_SCALE
        }
        fun modelRotationX(v: Float) = apply {
            record.modelRotationXField = if (v.isFinite()) v else DEFAULT_MODEL_ROTATION
        }
        fun modelRotationY(v: Float) = apply {
            record.modelRotationYField = if (v.isFinite()) v else DEFAULT_MODEL_ROTATION
        }
        fun modelInteractive(v: Boolean) = apply { record.modelInteractiveField = v }
        fun modelBackgroundColor(v: Int) = apply { record.modelBackgroundColorField = v }
        fun effectKind(v: String?) = apply { record.effectKindField = v }
        fun effectColorA(v: Int) = apply { record.effectColorAField = v }
        fun effectColorB(v: Int) = apply { record.effectColorBField = v }
        fun effectIntensity(v: Float) = apply { record.effectIntensityField = v }
        fun effectFrequency(v: Float) = apply { record.effectFrequencyField = v }
        fun effectSpeed(v: Float) = apply { record.effectSpeedField = v }
        fun effectProgress(v: Float) = apply { record.effectProgressField = v }
        fun effectCornerRadii(v: FloatArray?) = apply { record.effectCornerRadiiField = v }
        fun effectCenterX(v: Float) = apply { record.effectCenterXField = v }
        fun effectCenterY(v: Float) = apply { record.effectCenterYField = v }
        fun effectRadius(v: Float) = apply { record.effectRadiusField = v }
        fun effectMode(v: Int) = apply { record.effectModeField = v }
        fun effectDensity(v: Float) = apply { record.effectDensityField = v }
        fun effectSize(v: Float) = apply { record.effectSizeField = v }
        fun effectAnimated(v: Boolean) = apply { record.effectAnimatedField = v }
        fun uv(x: Float, y: Float, w: Float, h: Float) = apply {
            record.uvXField = x
            record.uvYField = y
            record.uvWField = w
            record.uvHField = h
        }

        fun materialKind(v: String?) = apply { record.materialKindField = v }
        fun materialFields(v: Map<String, *>?) = apply { record.materialFieldsField = v }
        fun particleElementId(v: String) = apply { record.particleElementIdField = v }
        fun particleOrdinal(v: Int) = apply { record.particleOrdinalField = v }
        fun particleSpec(v: Map<String, *>?) = apply { record.particleSpecField = v }
        fun richTextMaxWidth(v: Float) = apply { record.richTextMaxWidthField = v }
        fun richTextMultiline(v: Boolean) = apply { record.richTextMultilineField = v }
        fun richTextLineStep(v: Float) = apply { record.richTextLineStepField = v }
        fun blurRadius(v: Float) = apply { record.blurRadiusField = v }
        fun filterGroupCommands(v: List<DrawRecord>?) = apply { record.filterGroupCommandsField = v }
        fun backdropBlurRadius(v: Float) = apply { record.backdropBlurRadiusField = v }
        fun blendMode(v: Int) = apply { record.blendModeField = v }
        fun blendGroupCommands(v: List<DrawRecord>?) = apply { record.blendGroupCommandsField = v }
        fun historyElementId(v: String) = apply { record.historyElementIdField = v }
        fun historySpec(v: Map<String, *>?) = apply { record.historySpecField = v }
        fun historyGroupCommands(v: List<DrawRecord>?) = apply { record.historyGroupCommandsField = v }
        fun batchCommands(v: List<DrawRecord>?) = apply { record.batchCommandsField = v }
        fun sequence(v: Int) = apply { record.sequenceField = v }
        fun flushBarrier(v: Boolean) = apply { record.flushBarrierField = v }
        fun barrierPushZ(v: Float) = apply { record.barrierPushZField = v }
        fun barrierPopZ(v: Boolean) = apply { record.barrierPopZField = v }
        fun state(v: StateSnapshot?) = apply { record.stateField = v }
        internal fun reset() = apply { record.reset() }
        fun build(): DrawRecord = record
    }

    companion object {
        /** 数量徽标默认锚点：复刻 vanilla 右下角。 */
        private const val DEFAULT_COUNT_ANCHOR = "bottom-right"
        private const val DEFAULT_MODEL_SCALE = 1f
        private const val DEFAULT_MODEL_ROTATION = 0f

        @JvmStatic
        fun builder(): Builder = Builder()
    }
}
